import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';

import { type Location, useLocation, useNavigationType } from '@remix-run/react';

interface UseScrollMemoryOptions {
  // Return `null` to skip preservation for the current location.
  keyFor: (location: Location) => string | null;
}

// Per-element scroll restoration: saves scrollTop on location change, restores
// on POP only. Diverges from `~/components/ScrollRestoration` (which hooks
// `window.scrollY`) because the community layout scrolls a child
// `<main overflow-y-auto>` — `window.scrollY` is always 0 there. Keys
// per-location via caller-supplied `keyFor` so the user lands where they left
// a route across separate visits.
export function useScrollMemory<T extends HTMLElement = HTMLElement>({
  keyFor,
}: UseScrollMemoryOptions): RefObject<T | null> {
  const elementRef = useRef<T>(null);
  const lastScrollRef = useRef(0);
  const previousKeyRef = useRef<string | null | undefined>(undefined);
  const location = useLocation();
  const navigationType = useNavigationType();

  const currentKey = keyFor(location);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const onScroll = () => {
      lastScrollRef.current = element.scrollTop;
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigationType is read at effect-run time intentionally; we only want to react to currentKey changes
  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const previousKey = previousKeyRef.current;
    // `undefined` = first mount (no save yet); `null` = ran but caller opted out.
    if (previousKey === undefined) {
      previousKeyRef.current = currentKey;
      return;
    }
    if (previousKey === currentKey) return;

    if (previousKey != null) {
      sessionStorage.setItem(previousKey, String(lastScrollRef.current));
    }

    if (currentKey != null) {
      const saved = navigationType === 'POP' ? sessionStorage.getItem(currentKey) : null;
      const requested = saved != null ? Number(saved) : 0;
      // Fall back to 0 when content is shorter than the saved offset (e.g.
      // after a hard reload wiped the in-memory cache, leaving only seed
      // page 1). Without this we'd clamp to the bottom of partial content.
      const reachable = requested <= element.scrollHeight - element.clientHeight;
      const targetY = reachable ? requested : 0;
      element.scrollTo(0, targetY);
      lastScrollRef.current = targetY;
    } else {
      element.scrollTo(0, 0);
      lastScrollRef.current = 0;
    }

    previousKeyRef.current = currentKey;
  }, [currentKey]);

  return elementRef;
}
